import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import {
  Container,
  Box,
  Grid,
  Button,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import RFPUpload from '../components/RFPUpload';
import ProposalViewer from '../components/ProposalViewer';
import ProgressTracker from '../components/ProgressTracker';
import CompletionActions from '../components/CompletionActions';
import QuestionProgressView from '../components/QuestionProgressView';
import InsufficientInfoDialog from '../components/InsufficientInfoDialog';
import AnswerEditDialog from '../components/AnswerEditDialog';
import LogViewer, { type LogEntry } from '../components/LogViewer';
import PromptsDialog from '../components/PromptsDialog';
import PlanApprovalDialog from '../components/PlanApprovalDialog';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

interface RFPData {
  rfpId: string;
  filename: string;
  parsedData: {
    requirements: string[];
    deadline?: string;
    focusAreas: string[];
  };
}

interface ProposalProgress {
  rfpId: string;
  status: 'parsing' | 'analyzing' | 'generating' | 'completed' | 'error';
  progress: number;
  currentStep: string;
  error?: string;
}

interface Question {
  id: string;
  questionNumber: number;
  questionText: string;
  questionType: string;
  status: 'pending' | 'researching' | 'writing' | 'review' | 'completed' | 'insufficient_info' | 'skipped';
  answer?: string;
  answerMetadata?: {
    wordCount: number;
    targetWordCount?: number;
    sourcesCited: string[];
    confidenceScore: number;
  };
  retrievedContext?: Array<{
    documentTitle: string;
    relevanceScore: number;
  }>;
  insufficientInfo?: {
    availableContext: string;
    missingInformation: string[];
    suggestedSources: string[];
  };
  progress?: number;
  currentStep?: string;
}

export default function Generate() {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [rfpData, setRfpData] = useState<RFPData | null>(null);
  const [progress, setProgress] = useState<ProposalProgress | null>(null);
  const [proposalContent, setProposalContent] = useState<string>('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [proposalNarrative, setProposalNarrative] = useState<string>('');
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [proposalId, setProposalId] = useState<string | null>(null);
  const [promptsDialogOpen, setPromptsDialogOpen] = useState(false);
  const [prompts, setPrompts] = useState<{
    questionExtraction?: string;
    answerGeneration?: string;
    cohesionReview?: string;
  }>({});
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [planApprovalOpen, setPlanApprovalOpen] = useState(false);
  const [proposalPlan, setProposalPlan] = useState<unknown>(null);
  const [planningCost, setPlanningCost] = useState<number>(0);
  const [showRestoredBanner, setShowRestoredBanner] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Use ref for reset flag to avoid re-renders
  const isResettingRef = useRef(false);

  // Restore state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('proposalGenerationState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        // Only restore if the state is recent (within 24 hours)
        const stateAge = Date.now() - new Date(state.timestamp).getTime();
        if (stateAge < 24 * 60 * 60 * 1000) {
          // IMPROVED: Restore completed state with banner instead of clearing
          if (state.progress?.status === 'completed' && state.proposalContent) {
            setRfpData(state.rfpData);
            setProposalId(state.proposalId);
            setProposalNarrative(state.proposalNarrative);
            setProposalContent(state.proposalContent);

            // Show completed state, not generating state
            setProgress({
              ...state.progress,
              status: 'completed',
              currentStep: 'Proposal generated successfully!'
            });

            // Show info banner about restored state
            setShowRestoredBanner(true);
          }
          // Restore in-progress states
          else if (state.rfpData && (
            (state.progress && state.progress.status !== 'completed') ||
            (!state.progress && !state.proposalContent)
          )) {
            setRfpData(state.rfpData);
            setProposalId(state.proposalId);
            setProposalNarrative(state.proposalNarrative);
            setProposalContent(state.proposalContent);

            // Fix stuck generating states
            if (state.progress) {
              const isStuck =
                state.progress.status === 'generating' &&
                state.progress.progress === 100 &&
                state.proposalContent;

              if (isStuck) {
                console.log('Detected stuck generating state, auto-fixing to completed');
                setProgress({
                  ...state.progress,
                  status: 'completed',
                  currentStep: 'Proposal generated successfully!'
                });
              } else {
                setProgress(state.progress);
              }
            }
          } else {
            // Clear invalid/old state
            localStorage.removeItem('proposalGenerationState');
          }
        } else {
          // Clear old state
          localStorage.removeItem('proposalGenerationState');
        }
      } catch (error) {
        console.error('Failed to restore state:', error);
        localStorage.removeItem('proposalGenerationState');
      }
    }
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    // Don't save state during reset operations
    if (isResettingRef.current) {
      return;
    }

    if (rfpData || progress || proposalContent) {
      const state = {
        rfpData,
        progress,
        proposalContent,
        proposalId,
        proposalNarrative,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('proposalGenerationState', JSON.stringify(state));
    }
  }, [rfpData, progress, proposalContent, proposalId, proposalNarrative]);

  useEffect(() => {
    const newSocket = io(API_BASE_URL);
    setSocket(newSocket);

    // Legacy mode listeners
    newSocket.on('proposal-progress', (data: ProposalProgress) => {
      setProgress(data);
    });

    newSocket.on('proposal-update', (data: { rfpId: string; partial: string }) => {
      setProposalContent(prev => prev + data.partial);
    });

    // Question mode listeners
    newSocket.on('rfp-progress', (data: { rfpId: string; status: string; progress: number; message: string }) => {
      setProgress({
        rfpId: data.rfpId,
        status: data.status as 'parsing' | 'analyzing' | 'generating' | 'completed' | 'error',
        progress: data.progress,
        currentStep: data.message
      });
    });

    newSocket.on('questions-list', (data: { rfpId: string; questions: Question[] }) => {
      setQuestions(data.questions);
    });

    newSocket.on('question-progress', (data: Question & { rfpId: string }) => {
      setQuestions(prev =>
        prev.map(q =>
          q.id === data.id
            ? { ...q, status: data.status, progress: data.progress, currentStep: data.currentStep }
            : q
        )
      );
    });

    newSocket.on('question-answer', (data: {
      rfpId: string;
      questionId: string;
      answer: string;
      metadata: {
        wordCount: number;
        targetWordCount?: number;
        sourcesCited: string[];
        confidenceScore: number;
      }
    }) => {
      setQuestions(prev =>
        prev.map(q =>
          q.id === data.questionId
            ? { ...q, answer: data.answer, answerMetadata: data.metadata }
            : q
        )
      );
    });

    newSocket.on('proposal-complete', (data: { rfpId: string; content: string; proposalId?: string; questionSummary?: unknown }) => {
      setProposalContent(data.content);
      if (data.proposalId) {
        setProposalId(data.proposalId);
      }
      // Update progress to completed state
      setProgress({
        rfpId: data.rfpId,
        status: 'completed',
        progress: 100,
        currentStep: 'Proposal generated successfully!'
      });
      // Store question summary in state if needed for display
    });

    newSocket.on('proposal-error', (data: { rfpId: string; error: string }) => {
      setGenerationError(data.error);
      setProgress({
        rfpId: data.rfpId,
        status: 'error',
        progress: 0,
        currentStep: 'Failed',
        error: data.error
      });
    });

    // Claude Code log streaming
    newSocket.on('claude:log', (logEntry: LogEntry) => {
      // Only add logs for the current RFP
      if (rfpData && logEntry.rfpId === rfpData.rfpId) {
        setLogs(prev => [...prev, logEntry]);
      }
    });

    // Plan approval flow
    newSocket.on('proposal-plan-ready', (data: { plan: unknown; planningCost: number }) => {
      console.log('Proposal plan ready:', data);
      setProposalPlan(data.plan);
      setPlanningCost(data.planningCost);
      setPlanApprovalOpen(true);
    });

    return () => {
      newSocket.off('proposal-progress');
      newSocket.off('proposal-update');
      newSocket.off('rfp-progress');
      newSocket.off('questions-list');
      newSocket.off('question-progress');
      newSocket.off('question-answer');
      newSocket.off('proposal-complete');
      newSocket.off('proposal-error');
      newSocket.off('claude:log');
      newSocket.off('proposal-plan-ready');
      newSocket.close();
    };
  }, [rfpData]);

  const handleRFPUploaded = (data: RFPData) => {
    setRfpData(data);
    setProposalContent('');
    setQuestions([]);
    setLogs([]); // Clear logs for new RFP
    setProposalId(null); // Clear proposalId for new RFP
  };

  const handleGenerateProposal = async () => {
    if (!rfpData || progress) return; // Prevent double-click

    // Immediately set progress to disable button
    setProgress({
      rfpId: rfpData.rfpId,
      status: 'parsing',
      progress: 0,
      currentStep: 'Starting proposal generation...'
    });

    setGenerationError(null);

    try {
      // First update the RFP with narrative if provided
      if (proposalNarrative.trim()) {
        await axios.patch(
          API_ENDPOINTS.rfp.update(rfpData.rfpId),
          { proposalNarrative },
          { withCredentials: true }
        );
      }

      await axios.post(
        API_ENDPOINTS.rfp.generate(rfpData.rfpId),
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to start generation:', error);

      // Handle specific error types from the backend
      if (axios.isAxiosError(error) && error.response) {
        const errorData = error.response.data;

        // Missing context documents
        if (error.response.status === 400) {
          setGenerationError(
            errorData.error ||
            'Unable to generate proposal. Please upload context documents on the Context page before generating a proposal.'
          );
          return;
        }

        // Workspace initialization error
        if (error.response.status === 500 && errorData.error?.includes('workspace')) {
          setGenerationError(
            'Failed to initialize workspace. Please try uploading context documents again on the Context page.'
          );
          return;
        }

        // Use backend error message if available
        if (errorData.error || errorData.message) {
          setGenerationError(errorData.error || errorData.message);
          return;
        }
      }

      // Generic fallback error
      setGenerationError('Failed to start proposal generation. Please try again or contact support if the issue persists.');
      // Reset progress on error so user can try again
      setProgress(null);
    }
  };

  const handleReset = () => {
    // Set reset flag to prevent state persistence during reset
    isResettingRef.current = true;

    // Clear saved state FIRST to prevent race conditions
    localStorage.removeItem('proposalGenerationState');

    // Then clear all state
    setRfpData(null);
    setProgress(null);
    setProposalContent('');
    setQuestions([]);
    setGenerationError(null);
    setLogs([]);
    setProposalId(null);
    setProposalNarrative('');
    setShowRestoredBanner(false);

    // Reset the flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isResettingRef.current = false;
    }, 100);
  };

  const handleClearLogs = () => {
    setLogs([]);
  };

  const handleRegenerateAnswer = async (questionId: string) => {
    try {
      await axios.post(
        API_ENDPOINTS.rfp.regenerate(questionId),
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to regenerate answer:', error);
    }
  };

  const handleSkipQuestion = async (questionId: string) => {
    try {
      await axios.post(
        API_ENDPOINTS.rfp.skip(questionId),
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to skip question:', error);
    }
  };

  const handleEditAnswer = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      setSelectedQuestion(question);
      setEditDialogOpen(true);
    }
  };

  const handleProvideContext = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      setSelectedQuestion(question);
      setInfoDialogOpen(true);
    }
  };

  const handleSubmitContext = async (context: string) => {
    if (!selectedQuestion) return;

    try {
      await axios.patch(
        API_ENDPOINTS.rfp.question(selectedQuestion.id),
        { manualContext: context },
        { withCredentials: true }
      );

      // Trigger regeneration
      await handleRegenerateAnswer(selectedQuestion.id);
    } catch (error) {
      console.error('Failed to submit context:', error);
    }
  };

  const handleSaveAnswer = async (editedAnswer: string) => {
    if (!selectedQuestion) return;

    try {
      await axios.patch(
        API_ENDPOINTS.rfp.question(selectedQuestion.id),
        { answer: editedAnswer },
        { withCredentials: true }
      );

      setQuestions(prev =>
        prev.map(q =>
          q.id === selectedQuestion.id ? { ...q, answer: editedAnswer } : q
        )
      );
    } catch (error) {
      console.error('Failed to save answer:', error);
    }
  };

  const handleViewPrompts = async () => {
    if (!proposalId) {
      return;
    }

    setLoadingPrompts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/rfp/history/${proposalId}/prompts`, {
        withCredentials: true,
      });
      setPrompts(response.data.prompts || {});
      setPromptsDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      setPrompts({});
      setPromptsDialogOpen(true);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleApprovePlan = () => {
    if (socket && rfpData) {
      socket.emit('plan-approved', {
        rfpId: rfpData.rfpId,
        plan: proposalPlan
      });
      setPlanApprovalOpen(false);
    }
  };

  const handleRejectPlan = () => {
    if (socket && rfpData) {
      socket.emit('plan-rejected', {
        rfpId: rfpData.rfpId
      });
      setPlanApprovalOpen(false);
      setGenerationError('Plan rejected. Generation cancelled.');
      setProgress(null);
    }
  };

  const handleSavePlan = () => {
    // Close the dialog and reset to upload screen
    setPlanApprovalOpen(false);
    setProgress(null);
    // Plan is already saved in localStorage via the persistence mechanism
    // Show success message
    alert('Plan saved successfully! You can return anytime to approve and generate the proposal.');
  };

  const handleCancelGeneration = async () => {
    if (!rfpData || !window.confirm('Are you sure you want to cancel this proposal generation?')) {
      return;
    }

    setIsCancelling(true);

    try {
      // Call backend to cancel generation
      await axios.post(
        API_ENDPOINTS.rfp.cancel(rfpData.rfpId),
        {},
        { withCredentials: true }
      );

      // Emit socket event for real-time cancellation
      if (socket) {
        socket.emit('cancel-generation', { rfpId: rfpData.rfpId });
      }

      // Reset local state
      handleReset();
    } catch (error) {
      console.error('Failed to cancel generation:', error);
      setGenerationError('Failed to cancel generation. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {!rfpData || !progress ? (
        <RFPUpload
          onRFPUploaded={handleRFPUploaded}
          proposalNarrative={proposalNarrative}
          setProposalNarrative={setProposalNarrative}
          rfpData={rfpData}
          onGenerateProposal={handleGenerateProposal}
          onReset={handleReset}
          isGenerating={progress !== null}
        />
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Stack spacing={3}>
                {generationError && (
                  <Alert
                    severity="error"
                    onClose={() => setGenerationError(null)}
                    action={
                      generationError.toLowerCase().includes('timeout') ? (
                        <Button
                          color="inherit"
                          size="small"
                          href="/settings"
                          sx={{ textDecoration: 'underline' }}
                        >
                          Adjust Timeout
                        </Button>
                      ) : undefined
                    }
                  >
                    {generationError}
                    {generationError.toLowerCase().includes('timeout') && (
                      <Typography variant="caption" display="block" mt={1}>
                        💡 You can increase the timeout limit in Settings to handle larger proposals.
                      </Typography>
                    )}
                  </Alert>
                )}

                {showRestoredBanner && progress?.status === 'completed' && (
                  <Alert
                    severity="info"
                    onClose={() => setShowRestoredBanner(false)}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body2">
                      Restored your recently completed proposal. View it below or{' '}
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => window.location.href = '/app/history'}
                        sx={{ textDecoration: 'underline', p: 0, minWidth: 'auto', textTransform: 'none' }}
                      >
                        check your history
                      </Button>
                      .
                    </Typography>
                  </Alert>
                )}

                {progress && progress.status === 'completed' ? (
                  <CompletionActions
                    rfpName={rfpData.filename}
                    proposalId={proposalId || undefined}
                    onGenerateNew={handleReset}
                  />
                ) : progress ? (
                  <Box>
                    <ProgressTracker progress={progress} />
                    {/* Show Prompts button when generation is in progress */}
                    {proposalId && progress.status === 'generating' && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<CodeIcon />}
                          onClick={handleViewPrompts}
                          disabled={loadingPrompts}
                          sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                        >
                          View Prompts
                        </Button>
                      </Box>
                    )}
                    {/* Cancel button for in-progress generation */}
                    {progress.status !== 'completed' && (
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleCancelGeneration}
                          disabled={isCancelling}
                          size="small"
                        >
                          {isCancelling ? 'Cancelling...' : 'Cancel Generation'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : null}

                {/* Log Viewer - only show when there are logs or generation is in progress */}
                {(logs.length > 0 || (progress && progress.status === 'generating')) && (
                  <LogViewer
                    logs={logs}
                    onClear={handleClearLogs}
                    defaultExpanded={false}
                  />
                )}

                {questions.length > 0 && (
                  <QuestionProgressView
                    questions={questions}
                    onRegenerate={handleRegenerateAnswer}
                    onSkip={handleSkipQuestion}
                    onEdit={handleEditAnswer}
                    onProvideContext={handleProvideContext}
                  />
                )}

                {proposalContent && (
                  <ProposalViewer
                    content={proposalContent}
                    rfpId={rfpData.rfpId}
                    proposalId={proposalId || undefined}
                    rfpName={rfpData.filename}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </>
      )}

      {selectedQuestion && (
        <>
          <InsufficientInfoDialog
            open={infoDialogOpen}
            questionText={selectedQuestion.questionText}
            insufficientInfo={selectedQuestion.insufficientInfo}
            onClose={() => {
              setInfoDialogOpen(false);
              setSelectedQuestion(null);
            }}
            onSubmit={handleSubmitContext}
          />

          <AnswerEditDialog
            open={editDialogOpen}
            questionNumber={selectedQuestion.questionNumber}
            questionText={selectedQuestion.questionText}
            currentAnswer={selectedQuestion.answer || ''}
            answerMetadata={selectedQuestion.answerMetadata}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedQuestion(null);
            }}
            onSave={handleSaveAnswer}
          />
        </>
      )}

      <PromptsDialog
        open={promptsDialogOpen}
        rfpName={rfpData?.filename || 'Proposal'}
        prompts={prompts}
        onClose={() => setPromptsDialogOpen(false)}
      />

      <PlanApprovalDialog
        open={planApprovalOpen}
        plan={proposalPlan as { questions: Array<{ number: number; text: string; approach: string }>; strategy: string; estimatedCost: string } | null}
        planningCost={planningCost}
        rfpName={rfpData?.filename || ''}
        onApprove={handleApprovePlan}
        onReject={handleRejectPlan}
        onSavePlan={handleSavePlan}
      />
    </Container>
  );
}
